import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { FileField } from "./FileField";
import { FocusPicker } from "./FocusPicker";
import { PagesField } from "./PagesField";

const EMPTY = { title: "", category: "art", year: "", description: "", cover_url: "", pdf_url: "", video_url: "", images: [], tags: [], featured: false, order: 0, focus_x: 50, focus_y: 50 };
const field = "w-full bg-[#0B0C10] border border-[#F5A524]/30 focus:border-[#F5A524] outline-none px-3 py-2.5 text-sm text-white";
const Label = ({ children }) => <span className="block font-mono text-[11px] tracking-[0.25em] text-zinc-400 mb-2">{children}</span>;

export const ProjectForm = ({ initial, onSaved, onCancel }) => {
  const [f, setF] = useState(() => ({ ...EMPTY, ...(initial || {}) }));
  const [tags, setTags] = useState((initial?.tags || []).join(", "));
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.cover_url) return toast.error("Cover image is required");
    setBusy(true);
    const { id, created_at, ...rest } = f;
    const body = { ...rest, order: Number(f.order) || 0, tags: tags.split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      if (initial?.id) await api.put(`/projects/${initial.id}`, body);
      else await api.post("/projects", body);
      toast.success(initial?.id ? "PROJECT UPDATED" : "PROJECT CREATED");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="project-form" className="space-y-5">
      <label className="block"><Label>TITLE</Label>
        <input data-testid="form-title-input" required value={f.title} onChange={(e) => set("title")(e.target.value)} className={field} />
      </label>
      <div className="grid grid-cols-3 gap-4">
        <label className="block"><Label>CATEGORY</Label>
          <select data-testid="form-category-select" value={f.category} onChange={(e) => set("category")(e.target.value)} className={field}>
            <option value="art">Art</option><option value="comic">Comic</option><option value="vignette">Vignette</option><option value="animation">Animation</option>
          </select>
        </label>
        <label className="block"><Label>YEAR</Label>
          <input data-testid="form-year-input" value={f.year} onChange={(e) => set("year")(e.target.value)} className={field} />
        </label>
        <label className="block"><Label>ORDER</Label>
          <input data-testid="form-order-input" type="number" value={f.order} onChange={(e) => set("order")(e.target.value)} className={field} />
        </label>
      </div>
      <label className="block"><Label>DESCRIPTION</Label>
        <textarea data-testid="form-description-input" rows={4} value={f.description} onChange={(e) => set("description")(e.target.value)} className={field} />
      </label>
      <label className="block"><Label>TAGS (COMMA SEPARATED)</Label>
        <input data-testid="form-tags-input" value={tags} onChange={(e) => setTags(e.target.value)} className={field} />
      </label>
      <FileField label="COVER IMAGE *" kind="image" accept="image/*" value={f.cover_url} onChange={set("cover_url")} testid="form-cover" />
      {f.cover_url && <FocusPicker src={f.cover_url} x={f.focus_x ?? 50} y={f.focus_y ?? 50} onChange={(x, y) => setF((p) => ({ ...p, focus_x: x, focus_y: y }))} />}
      <div className="border border-[#F5A524]/20 p-4">
        <PagesField pages={f.images ?? []} onChange={set("images")} label="EXTRA IMAGES — OPTIONAL"
          help="More views, details or versions of this piece. They appear in a gallery after the cover, in this order." button="ADD IMAGES" />
      </div>
      <FileField label="VIDEO — OPTIONAL (UPLOAD UP TO 200MB, OR PASTE A YOUTUBE LINK)" kind="video" accept="video/mp4,video/webm,video/quicktime,.mov,.m4v" value={f.video_url} onChange={set("video_url")} testid="form-video" />
      <FileField label="PDF (COMIC / VIGNETTE) — OPTIONAL" kind="pdf" accept="application/pdf" value={f.pdf_url} onChange={set("pdf_url")} testid="form-pdf" />
      <label className="flex items-center gap-3 cursor-pointer">
        <input data-testid="form-featured-checkbox" type="checkbox" checked={f.featured} onChange={(e) => set("featured")(e.target.checked)} className="w-4 h-4 accent-[#F5A524]" />
        <span className="font-mono text-xs tracking-[0.2em] text-zinc-300">FEATURED (SHOWN FIRST)</span>
      </label>
      <div className="flex gap-3 pt-2">
        <button data-testid="form-submit-btn" disabled={busy} className="flex-1 clip-tab bg-[#F5A524] text-black font-mono font-bold tracking-[0.2em] py-3 hover:bg-white disabled:opacity-60 transition-colors">
          {busy ? "SAVING..." : "SAVE PROJECT"}
        </button>
        <button type="button" data-testid="form-cancel-btn" onClick={onCancel} className="px-6 border border-[#F5A524]/30 font-mono text-xs tracking-[0.2em] text-zinc-300 hover:border-[#F5A524]">CANCEL</button>
      </div>
    </form>
  );
};
